/**
 * Focus management for a non-native dialog: trap Tab inside it while open, and
 * hand focus back to the control that opened it on close.
 *
 * Without this a keyboard user Tabs straight out of the popover into the
 * page behind it, which is still fully interactive, and closing strands them
 * at the top of the document instead of on the trigger they just used.
 *
 * Deliberately does NOT steal focus on open: the composer carries autoFocus, so
 * moving focus here would fight it. Focus is only placed if it is not already
 * inside the dialog.
 *
 * Shared by dashboard help and extension help, including its shadow root.
 */
import { useEffect } from 'react';
import { getUiActiveElement } from '../shadowDom';
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
function activeElementWithin(root) {
    // document.activeElement is the host while focus is inside a shadow root.
    // Read the root we already own; this also works when that root is closed.
    const tree = root.getRootNode();
    return getUiActiveElement('activeElement' in tree
        ? tree
        : root.ownerDocument);
}
function focusableWithin(root) {
    const active = activeElementWithin(root);
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === active);
}
export function useDialogFocus(isOpen, dialogRef, returnFocusRef, 
// The extension supplies its trusted-input policy; web dialogs keep their
// normal listener without depending on extension-only build flags.
wrapKeyboardListener) {
    // Place focus inside the dialog if nothing there has it yet.
    useEffect(() => {
        if (!isOpen)
            return;
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        if (dialog.contains(activeElementWithin(dialog)))
            return;
        focusableWithin(dialog)[0]?.focus();
    }, [isOpen, dialogRef]);
    // Trap Tab / Shift+Tab.
    useEffect(() => {
        if (!isOpen)
            return;
        const owner = dialogRef.current?.ownerDocument;
        if (!owner)
            return;
        const onKeyDown = (e) => {
            if (e.key !== 'Tab')
                return;
            const dialog = dialogRef.current;
            if (!dialog)
                return;
            const items = focusableWithin(dialog);
            if (items.length === 0)
                return;
            const first = items[0];
            const last = items[items.length - 1];
            const active = activeElementWithin(dialog);
            // Focus outside the dialog (or on its edges) wraps back inside.
            if (!active || !dialog.contains(active)) {
                e.preventDefault();
                (e.shiftKey ? last : first).focus();
                return;
            }
            if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
            else if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            }
        };
        const listener = wrapKeyboardListener?.(onKeyDown) ?? onKeyDown;
        owner.addEventListener('keydown', listener, true);
        return () => owner.removeEventListener('keydown', listener, true);
    }, [isOpen, dialogRef, wrapKeyboardListener]);
    // Restore focus to the trigger on the open -> closed transition (the cleanup
    // of the open state), so a keyboard user lands back on the control they used
    // rather than at the top of the document.
    useEffect(() => {
        if (!isOpen)
            return;
        return () => {
            returnFocusRef.current?.focus();
        };
    }, [isOpen, returnFocusRef]);
}
//# sourceMappingURL=useDialogFocus.js.map